import axios from 'axios';
import {
  PARTNER_MGMT_API_URL,
  PARTNER_MGMT_GDPR_API_URL,
  PARTNER_MGMT_LE_API_URL,
  PARTNER_MGMT_PI_API_URL,
} from 'services/constants';
import { getPartnerName, PartnerType } from 'types/Partner';
import PartnerNameType from 'types/PartnerNameType';
import { LegalPartner } from 'types/LegalPartner';
import { getHostBaseUrl, removeEmptyProperties } from 'utils/browserUtil';
import PrivateIndividual from 'types/PrivateIndividual';
import { CONTENT_TYPE_JSON } from 'services/userFeedbackService';

const axiosInstance = axios.create({ baseURL: getHostBaseUrl(), withCredentials: true });

/**
 * Fetch a private individual
 */
const getPartner = async (partnerId: string) => {
  const response = await axiosInstance.get(`${PARTNER_MGMT_PI_API_URL}/${partnerId}`);
  return {
    ...response.data,
    firstName: getPartnerName(response.data, PartnerNameType.FIRST_NAME),
    lastName: getPartnerName(response.data, PartnerNameType.LAST_NAME),
  };
};

const getLegalPartner = async (partnerId: string) => {
  const response = await axiosInstance.get(`${PARTNER_MGMT_LE_API_URL}/${partnerId}`);
  return {
    ...response.data,
    name: getPartnerName(response.data, PartnerNameType.LEGAL_NAME),
  };
};

/**
 * Get the partner list short form (partner names and their global partner IDs) for contact note creation
 */
const getShortMyTeamPartners = async () => {
  const response = await axiosInstance.get(`${PARTNER_MGMT_API_URL}/partners/dropdown?includeTeamPartners=true`);
  return response.data;
};

/**
 * Call the pcm-prospect-frontend-controller to modify an existing structure partner
 */
const updateLegalPartner = async (editedPartner: LegalPartner) => {
  removeEmptyProperties(editedPartner);
  const request = axiosInstance.put(`${PARTNER_MGMT_LE_API_URL}/${editedPartner.techId}`, editedPartner);
  const response = await request;
  return {
    ...response.data,
    name: getPartnerName(response.data, PartnerNameType.LEGAL_NAME),
  };
};

/**
 * Modify an existing private individual
 */
const updatePrivateIndividual = async (editedPartner: PrivateIndividual) => {
  removeEmptyProperties(editedPartner);
  const request = axiosInstance.put(`${PARTNER_MGMT_PI_API_URL}/${editedPartner.techId}`, { ...editedPartner });
  const response = await request;
  return {
    ...response.data,
    firstName: getPartnerName(response.data, PartnerNameType.FIRST_NAME),
    lastName: getPartnerName(response.data, PartnerNameType.LAST_NAME),
  };
};

/**
 * Call the pcm-prospect-frontend-controller to get the GDPR data of a partner
 */
const getGdprData = async (caseID: string, globalPartnerId: string, partnerType: PartnerType) => {
  return await axiosInstance.get(
    `${PARTNER_MGMT_GDPR_API_URL}/${
      partnerType === 'INDIVIDUAL_PARTNER' ? 'individualpartner' : 'legalpartner'
    }/${globalPartnerId}/cases/${caseID}`,
    {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': CONTENT_TYPE_JSON,
      },
    },
  );
};

/**
 * Anonymize the GDPR data of a partner
 */
const removeGdprData = async (globalPartnerId: string, caseId: string, partnerType: PartnerType) => {
  const response = await axiosInstance.put(
    `${PARTNER_MGMT_GDPR_API_URL}/${
      partnerType === 'INDIVIDUAL_PARTNER' ? 'individualpartner' : 'legalpartner'
    }/${globalPartnerId}/anonymize?caseId=${caseId}`,
    {},
  );
  return response.data;
};

/**
 * Get the revision history of a partner
 */
export const getRevisionHistoryData = async (techId: string, partnerType: PartnerType) => {
  const response = await axiosInstance.get(
    `${
      partnerType === PartnerType.INDIVIDUAL_PARTNER ? PARTNER_MGMT_PI_API_URL : PARTNER_MGMT_LE_API_URL
    }/${techId}/revision`,
    {},
  );
  return response.data;
};

/**
 * Get the GDPR data of a partner
 */
const getGdprRequestHistory = async (globalPartnerId: string) => {
  const response = await axiosInstance.get(`${PARTNER_MGMT_GDPR_API_URL}/${globalPartnerId}/requests`, {});
  return response.data;
};

/**
 * Get available RM and FTH list
 */
const getThRm = async (userId: string) => {
  const url = `${PARTNER_MGMT_API_URL}/hr/employees/${userId}/members`;
  const response = await axiosInstance.get(url);
  return response.data;
};

/**
 * Get available RM and FTH list
 */
const getAllAvailableThRmRma = async () => {
  const url = `${PARTNER_MGMT_API_URL}/hr/employees`;
  const response = await axiosInstance.get(url);
  return response.data;
};

/**
 * Reassign the RM of a partner
 * @param partnerType the type of the partner
 * @param partnerTechId the uID of the partner to update
 * @param rmUid the u-number of the new RM
 */
export const updatePartnerRm = async (partnerType: PartnerType, partnerTechId: string, rmUid: string) => {
  const response = await axiosInstance.put(
    `${PARTNER_MGMT_API_URL}/partners/${
      partnerType === PartnerType.INDIVIDUAL_PARTNER ? 'individualpartners' : 'legalpartners'
    }/${partnerTechId}/assigned-rm/${rmUid}`,
    {},
  );
  return response.data;
};

const methods = {
  getLegalPartner,
  getPartner,
  getShortMyTeamPartners,
  updateLegalPartner,
  updatePartner: updatePrivateIndividual,
  getGdprData,
  removeGdprData,
  getGdprRequestHistory,
  getThRm,
  getAllAvailableThRmRma,
  getRevisionHistoryData,
  updatePartnerRm,
};

export default methods;
